import type { Asset, Clip, Project, Track } from '../types'
import type { GeneratedVoice, VoiceWord } from './api'
import { assetOf, clipEnd, clipFromAsset, newTrack, subtitleClip } from './model'
import { useEditor } from '../store/editor'

/** Audio track that holds the narration: the one called «Voz», else the first audio track. */
export function voiceTrack(p: Project): Track | undefined {
  const audio = p.tracks.filter((t) => t.kind === 'audio')
  return audio.find((t) => t.name === 'Voz') ?? audio[0]
}

function subtitleTracks(p: Project): Set<string> {
  return new Set(p.clips.filter((c) => c.kind === 'subtitle').map((c) => c.trackId))
}

/** Current narration text = the (edited) subtitles, in timeline order. */
export function scriptFromSubtitles(p: Project): string {
  return p.clips
    .filter((c) => c.kind === 'subtitle' && (c.rawText ?? '').trim())
    .sort((a, b) => a.startSec - b.startSec)
    .map((c) => c.rawText!.trim())
    .join(' ')
    .replace(/\s+/g, ' ')
}

/** The voice track still holds the audio imported from Vaideo (final mix: voice + music). */
export function voiceIsVaideoMix(p: Project): boolean {
  const tr = voiceTrack(p)
  return !!tr && p.clips.some((c) => c.trackId === tr.id && assetOf(p, c)?.source === 'vaideo')
}

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[^\p{L}\p{N}]/gu, '')

/**
 * TTS word timings carry bare words (no punctuation, sometimes no accents).
 * Map them back onto the tokens of the text the user typed, in order, so the
 * captions keep their punctuation and casing.
 */
export function alignToText(words: VoiceWord[], text: string): VoiceWord[] {
  const tokens = text.split(/\s+/).filter(Boolean)
  const out: VoiceWord[] = []
  let i = 0
  for (const w of words) {
    const nw = norm(w.text)
    let j = -1
    for (let k = i; k < Math.min(tokens.length, i + 4) && nw; k++) {
      const nt = norm(tokens[k])
      if (nt === nw || (nt.length > 0 && (nt.startsWith(nw) || nw.startsWith(nt)))) { j = k; break }
    }
    if (j < 0) { out.push({ ...w }); continue }
    if (j > i && out.length) out[out.length - 1].text += ' ' + tokens.slice(i, j).join(' ')
    out.push({ ...w, text: (j > i && !out.length ? tokens.slice(i, j).join(' ') + ' ' : '') + tokens[j] })
    i = j + 1
  }
  if (i < tokens.length && out.length) out[out.length - 1].text += ' ' + tokens.slice(i).join(' ')
  return out
}

/** Caption chunks (≤ maxWords / ≤ maxChars, cut at punctuation) timed by the voice's words. */
export function chunkWords(words: VoiceWord[], maxWords = 6, maxChars = 34): { text: string; start: number; end: number }[] {
  const out: { text: string; start: number; end: number }[] = []
  let cur: VoiceWord[] = []
  const flush = () => {
    if (cur.length) out.push({ text: cur.map((w) => w.text).join(' '), start: cur[0].start, end: cur[cur.length - 1].end })
    cur = []
  }
  for (const w of words) {
    const candidate = [...cur, w].map((x) => x.text).join(' ')
    if (cur.length && (cur.length >= maxWords || candidate.length > maxChars)) flush()
    cur.push(w)
    if (/[.!?…]$/.test(w.text) || (/[;:]$/.test(w.text) && cur.length >= 2)) flush()
  }
  flush()
  // avoid a dangling single word
  if (out.length > 1 && !out[out.length - 1].text.includes(' ')) {
    const last = out.pop()!
    out[out.length - 1].text += ' ' + last.text
    out[out.length - 1].end = last.end
  }
  return out
}

export interface ApplyVoiceOptions { replace: boolean; resubtitle: boolean }

/**
 * Put a generated voice on the timeline in one undo step: optionally remove the
 * old narration, rebuild the subtitles with the new word timings and, when the
 * old narration was Vaideo's final mix, lay the background music on its own track.
 */
export function applyGeneratedVoice(r: GeneratedVoice, text: string, opts: ApplyVoiceOptions): { voiceEnd: number; videoEnd: number } {
  let voiceEnd = 0
  let videoEnd = 0
  let newId = ''
  useEditor.getState().mutate((p) => {
    const voiceAsset: Asset = { id: r.voice.id, name: `Voz ${r.voiceInfo.name || ''}.mp3`.replace('  ', ' '), info: r.voice.info, source: 'tts' }
    p.assets.unshift(voiceAsset)

    let tr = voiceTrack(p)
    let start = 0
    if (tr && opts.replace) {
      const old = p.clips.filter((c) => c.trackId === tr!.id)
      if (old.length) start = Math.min(...old.map((c) => c.startSec))
      p.clips = p.clips.filter((c) => c.trackId !== tr!.id)
    } else if (tr && p.clips.some((c) => c.trackId === tr!.id)) {
      // keep the old narration: new voice goes on a fresh track
      tr = newTrack('audio', 'Voz nueva')
      p.tracks.push(tr)
    }
    if (!tr) {
      tr = newTrack('audio', 'Voz')
      p.tracks.push(tr)
    }
    const vclip = clipFromAsset(voiceAsset, tr.id, start)
    p.clips.push(vclip)
    newId = vclip.id
    voiceEnd = clipEnd(vclip)
    videoEnd = p.clips.filter((c) => c.kind === 'video' || c.kind === 'image').reduce((m, c) => Math.max(m, clipEnd(c)), 0)

    if (opts.resubtitle && r.words.length) {
      const subIds = subtitleTracks(p)
      let subTr = p.tracks.find((t) => subIds.has(t.id)) ?? p.tracks.find((t) => t.kind === 'video' && t.name === 'Subtítulos')
      p.clips = p.clips.filter((c) => c.kind !== 'subtitle')
      if (!subTr) {
        subTr = newTrack('video', 'Subtítulos')
        p.tracks.unshift(subTr)
      }
      const chunks = chunkWords(alignToText(r.words, text))
      const subs: Clip[] = chunks.map((ch, i) => {
        const s = start + ch.start
        const next = chunks[i + 1] ? start + chunks[i + 1].start : start + ch.end + 0.3
        return subtitleClip(subTr!.id, s, Math.max(0.3, Math.min(next, voiceEnd) - s - 0.02), ch.text)
      })
      p.clips.push(...subs)
    }

    if (r.music && !p.tracks.some((t) => t.kind === 'audio' && t.name === 'Música')) {
      const musicAsset: Asset = { id: r.music.id, name: 'Música de fondo.mp3', info: r.music.info, source: 'vaideo' }
      p.assets.unshift(musicAsset)
      const mt = newTrack('audio', 'Música')
      p.tracks.push(mt)
      const cover = Math.max(videoEnd, voiceEnd)
      const len = musicAsset.info.durationSec
      for (let t = 0; len > 0.5 && t < cover - 0.05; t += len) {
        const c = clipFromAsset(musicAsset, mt.id, t)
        c.durationSec = Math.min(len, cover - t)
        c.sourceOutSec = c.durationSec
        c.audio.volume = r.music.volume
        c.audio.fadeInSec = t === 0 ? 1 : 0
        c.audio.fadeOutSec = t + len >= cover - 0.05 ? Math.min(2.5, c.durationSec / 4) : 0
        p.clips.push(c)
      }
    }
  })
  useEditor.getState().set({ selection: newId ? [newId] : [] })
  return { voiceEnd, videoEnd }
}
